import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOptimizedQuery, createSupabaseQuery } from "@/hooks/useOptimizedQuery";

interface Reminder {
  id: string;
  user_id: string;
  title: string;
  content: string;
  color: string;
  position: number;
  due_date: string | null;
  is_completed: boolean;
  created_at: string;
  updated_at: string;
}

export const useReminders = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const fetchReminders = createSupabaseQuery<Reminder[]>(
    () => supabase
      .from('reminders')
      .select('*')
      .eq('user_id', user?.id || '')
      .order('position', { ascending: true })
      .order('created_at', { ascending: false }),
    (data) => data || []
  );

  const {
    data: reminders = [],
    isLoading: loading,
    refetch
  } = useOptimizedQuery<Reminder[]>({
    queryKey: ['reminders', user?.id],
    queryFn: fetchReminders,
    enabled: !!user,
  });

  const createReminder = async (title: string = "Untitled Reminder", content: string = "", color: string = "#ef4444", dueDate?: Date) => {
    if (!user) return;

    try {
      const maxPosition = Math.max(...reminders.map(r => r.position), -1);
      const { data, error } = await supabase
        .from('reminders')
        .insert({
          user_id: user.id,
          title: title.trim() || "Untitled Reminder",
          content: content.trim(),
          color,
          position: maxPosition + 1,
          due_date: dueDate?.toISOString() || null
        })
        .select()
        .single();

      if (error) throw error;
      if (data) {
        // Update React Query cache
        queryClient.setQueryData(['reminders', user.id], (old: Reminder[] = []) => [...old, data]);
      }
      return data;
    } catch (error) {
      console.error('Error creating reminder:', error);
    }
  };

  const updateReminder = async (id: string, updates: Partial<Pick<Reminder, 'title' | 'content' | 'color' | 'due_date' | 'is_completed'>>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('reminders')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        // Update React Query cache
        queryClient.setQueryData(['reminders', user.id], (old: Reminder[] = []) =>
          old.map(reminder => reminder.id === id ? data : reminder)
        );
      }
    } catch (error) {
      console.error('Error updating reminder:', error);
    }
  };

  const deleteReminder = async (id: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('reminders')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      // Update React Query cache
      queryClient.setQueryData(['reminders', user.id], (old: Reminder[] = []) =>
        old.filter(reminder => reminder.id !== id)
      );
    } catch (error) {
      console.error('Error deleting reminder:', error);
    }
  };

  const getDueAndOverdueCount = () => {
    const now = new Date();
    now.setHours(23, 59, 59, 999); // End of today
    
    return reminders.filter(reminder => 
      !reminder.is_completed && 
      reminder.due_date && 
      new Date(reminder.due_date) <= now
    ).length;
  };

  // Set up real-time subscription for reminders
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('reminders')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reminders',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Reminder change:', payload);
          // Invalidate and refetch to ensure consistency
          queryClient.invalidateQueries({ queryKey: ['reminders', user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  return {
    reminders,
    loading,
    createReminder,
    updateReminder,
    deleteReminder,
    getDueAndOverdueCount,
    refetch
  };
};